import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { useMemo, useState } from "react";

import { Badge } from "../../../ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../../ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../ui/tooltip";
import type {
  DealerCreditQuality,
  DealerOfficeBreakdown,
  DealerVelocity,
} from "../lib/api/types";
import { cn } from "../lib/utils";

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const TIER_COLORS: Record<string, string> = {
  A: "hsl(var(--chart-2))",
  B1: "var(--amber)",
  B2: "hsl(var(--chart-5))",
  B3: "hsl(var(--chart-4))",
};

function TierBadge({ tier }: { tier: string }) {
  const color = TIER_COLORS[tier] ?? "var(--neutral-inactive)";
  return (
    <Badge
      variant="outline"
      className="px-2 py-0.5 text-[11px]"
      style={{
        borderColor: `color-mix(in oklch, ${color} 55%, transparent)`,
        background: `color-mix(in oklch, ${color} 16%, transparent)`,
        color,
      }}
    >
      {tier}
    </Badge>
  );
}

function SortIcon({ state }: { state: false | "asc" | "desc" }) {
  if (state === "asc") return <ArrowUp className="size-3.5" />;
  if (state === "desc") return <ArrowDown className="size-3.5" />;
  return <ChevronsUpDown className="size-3.5 opacity-50" />;
}

/* ── Section 1: credit quality table + office breakdown drawer ───────────── */

const columnHelper = createColumnHelper<DealerCreditQuality>();

export function DealerCreditQualityTable({
  rows,
  breakdownByDealer,
  delinquencyNote,
}: {
  rows: DealerCreditQuality[];
  breakdownByDealer: Record<string, DealerOfficeBreakdown[]>;
  delinquencyNote: string;
}) {
  const [sorting, setSorting] = useState<SortingState>([{ id: "volumeCount", desc: true }]);
  const [openDealer, setOpenDealer] = useState<string | null>(null);

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("dealerId", {
        header: "Dealer ID",
        cell: (info) => <span className="font-medium tabular-nums">{info.getValue()}</span>,
      }),
      columnHelper.accessor("modeRiskTier", {
        header: "Mode Risk Tier",
        cell: (info) => <TierBadge tier={info.getValue()} />,
      }),
      columnHelper.accessor("approvalRatePct", {
        header: "Approval Rate",
        cell: (info) => <span className="tabular-nums">{info.getValue()}%</span>,
      }),
      columnHelper.accessor("volumeCount", {
        header: "Volume",
        cell: (info) => (
          <span className="tabular-nums">
            {info.getValue()}{" "}
            <span className="text-muted-foreground">· {info.row.original.volumeSharePct}%</span>
          </span>
        ),
      }),
      columnHelper.display({
        id: "earlyDelinquency",
        header: "Early Delinquency",
        cell: () => (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className="cursor-help text-xs underline decoration-dotted underline-offset-4"
                  style={{ color: "var(--neutral-inactive)" }}
                >
                  Requires aging
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">{delinquencyNote}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ),
      }),
    ] as ColumnDef<DealerCreditQuality, any>[];
  }, [delinquencyNote]);

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const breakdown = openDealer ? (breakdownByDealer[openDealer] ?? []) : [];

  return (
    <>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((group) => (
              <TableRow key={group.id}>
                {group.headers.map((header) => {
                  const sortable = header.column.getCanSort();
                  return (
                    <TableHead key={header.id}>
                      {sortable ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="inline-flex items-center gap-1.5 hover:text-foreground"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          <SortIcon state={header.column.getIsSorted()} />
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                onClick={() => setOpenDealer(row.original.dealerId)}
                className="cursor-pointer"
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Sheet open={openDealer != null} onOpenChange={(open) => !open && setOpenDealer(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>{openDealer}</SheetTitle>
            <SheetDescription>
              Volume routed by this dealer across offices. Credit-quality figures are available
              everywhere; exception rate only starts once an office is live.
            </SheetDescription>
          </SheetHeader>
          <div className="px-4 pb-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Office</TableHead>
                  <TableHead className="text-right">Volume</TableHead>
                  <TableHead className="text-right">Approval Rate</TableHead>
                  <TableHead className="text-right">Exception Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {breakdown.map((entry) => (
                  <TableRow key={entry.officeName}>
                    <TableCell
                      className={cn(entry.exceptionRatePct == null && "text-muted-foreground")}
                    >
                      {entry.officeName}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{entry.volumeCount}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {entry.approvalRatePct}%
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {entry.exceptionRatePct == null ? (
                        <span style={{ color: "var(--neutral-inactive)" }}>Not yet live</span>
                      ) : (
                        `${entry.exceptionRatePct}%`
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

/* ── Section 2: supporting velocity detail table ─────────────────────────── */

export function DealerVelocityTable({ rows }: { rows: DealerVelocity[] }) {
  const sorted = [...rows].sort((a, b) => a.dealerId.localeCompare(b.dealerId));

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Dealer ID</TableHead>
            <TableHead className="text-right">Median First Decision</TableHead>
            <TableHead className="text-right">Median Final Decision</TableHead>
            <TableHead className="text-right">Avg Ticket Size</TableHead>
            {/* Exception rate was previously only in the charts. It is a real, measured
                number, and now that override rate is not, it earns a column. */}
            <TableHead className="text-right">Exception Rate</TableHead>
            <TableHead className="text-right">Override Rate</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((row) => (
            <TableRow key={row.dealerId}>
              <TableCell className="font-medium tabular-nums">{row.dealerId}</TableCell>
              <TableCell className="text-right tabular-nums">
                {row.medianFirstDecisionHours} h
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {row.medianFinalDecisionHours} h
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {currency.format(row.avgTicketSize)}
              </TableCell>
              <TableCell className="text-right tabular-nums">{row.exceptionRatePct}%</TableCell>
              {/* An em dash, not "null%" and not 0%. Override rate is unmeasurable on this
                  data (see DealerVelocity.overrideRatePct), and a 0 would read as "this
                  dealer never triggers an override" — a claim we cannot make. Rendering
                  `{value}%` directly would have printed a bare "%", since React renders
                  null as nothing. */}
              <TableCell className="text-right tabular-nums text-muted-foreground">
                {row.overrideRatePct == null ? (
                  <span title="Not measurable: Athena's recommendation column carries no signal on this data.">
                    —
                  </span>
                ) : (
                  `${row.overrideRatePct}%`
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
